// Client for the tracking assistant (service-tracking-assistant): WebSocket events, voice in and out.
// Audio is PCM16 mono 24 kHz both ways, the same format the service uses with GPT Realtime.

export type AssistantCardKind = 'status' | 'timeline' | 'delivery' | 'merchant'

export interface AssistantCard {
  kind: AssistantCardKind
  [key: string]: unknown
}

export interface AssistantEvent {
  type: string
  session?: string
  id?: string
  text?: string
  item?: string
  channel?: string
  card?: AssistantCard
  label?: string
  message?: string
  remaining?: number | null
  state?: string
  verified?: boolean
  voice?: boolean
  callOut?: boolean
  replay?: AssistantEvent[]
  draft?: string
  butik?: string
}

type Listener = (event: AssistantEvent) => void

const WORKLET = `
class Capture extends AudioWorkletProcessor {
  process(inputs) { const ch = inputs[0] && inputs[0][0]; if (ch) this.port.postMessage(ch.slice(0)); return true }
}
class Player extends AudioWorkletProcessor {
  constructor() {
    super(); this.q = []; this.cur = null; this.pos = 0; this.n = 0; this.sum = 0
    this.port.onmessage = (e) => { if (e.data === 'clear') { this.q = []; this.cur = null } else this.q.push(e.data) }
  }
  process(_, outputs) {
    const out = outputs[0][0]
    for (let i = 0; i < out.length; i++) {
      if (!this.cur || this.pos >= this.cur.length) { this.cur = this.q.shift() || null; this.pos = 0 }
      const v = this.cur ? this.cur[this.pos++] : 0
      out[i] = v; this.sum += v * v
    }
    this.n += out.length
    if (this.n >= 1200) { this.port.postMessage({ playing: !!this.cur || this.q.length > 0 }); this.n = 0; this.sum = 0 }
    return true
  }
}
registerProcessor('capture', Capture)
registerProcessor('player', Player)`

export default class AssistantClient {
  public sessionId = ''
  public live = false
  public speaking = false

  private ws: WebSocket | null = null
  private listeners: Listener[] = []
  private ctx: AudioContext | null = null
  private ready: Promise<void> | null = null
  private player: AudioWorkletNode | null = null
  private mic: MediaStream | null = null
  private capture: AudioWorkletNode | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private sink: GainNode | null = null
  private pending: Float32Array[] = []
  private pendingLength = 0
  private closed = false
  private retry = 0
  private queue: string[] = []

  constructor(
    private readonly baseUrl: string,
    private readonly trackingId: string,
    private readonly brand: string,
    private readonly lang: string,
  ) {}

  public on(listener: Listener): () => void {
    this.listeners.push(listener)
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener)
    }
  }

  public connect(): void {
    if (this.ws) return
    this.closed = false
    const url = new URL('/ws', this.baseUrl.replace(/^http/, 'ws'))
    url.searchParams.set('tracking', this.trackingId)
    url.searchParams.set('brand', this.brand)
    url.searchParams.set('lang', this.lang)
    if (this.sessionId) url.searchParams.set('session', this.sessionId)
    const ws = new WebSocket(url.toString())
    ws.binaryType = 'arraybuffer'
    ws.onopen = () => {
      this.retry = 0
      this.queue.splice(0).forEach((data) => ws.send(data))
    }
    ws.onmessage = (message) => {
      if (message.data instanceof ArrayBuffer) {
        void this.play(message.data)
        return
      }
      const event = JSON.parse(message.data as string) as AssistantEvent
      if (event.type === 'hello' && event.session) this.sessionId = event.session
      if (event.type === 'audio_clear') this.clearAudio()
      if (event.type === 'live' && event.state === 'off') this.stopMic(false)
      this.listeners.forEach((l) => l(event))
    }
    ws.onclose = () => {
      if (this.ws !== ws) return
      this.ws = null
      this.stopMic(false)
      if (!this.closed) setTimeout(() => this.connect(), Math.min(8000, 500 * 2 ** this.retry++))
    }
    this.ws = ws
  }

  public close(): void {
    this.closed = true
    this.stopMic()
    this.ws?.close()
    this.ws = null
    this.queue = []
    void this.ctx?.close()
    this.ctx = null
    this.ready = null
  }

  // Sends now, or once the connection is open (the page connects on the first interaction).
  public send(payload: Record<string, unknown>): void {
    const data = JSON.stringify(payload)
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(data)
      return
    }
    this.queue.push(data)
    this.connect()
  }

  public ask(text: string): void {
    void this.ensureAudio()
    this.send({ type: 'text', text })
  }

  public verify(postalCode: string): void {
    void this.ensureAudio()
    this.send({ type: 'verify', postalCode })
  }

  // ---- voice ----------------------------------------------------------------------------------
  public async startMic(): Promise<boolean> {
    try {
      await this.ensureAudio()
      if (!this.ctx) return false
      if (this.ctx.state !== 'running') await this.ctx.resume()
      this.mic = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      })
    } catch {
      return false
    }
    const track = this.mic.getAudioTracks()[0]
    if (track) {
      track.onended = () => {
        if (this.live) void this.restartMic()
      }
    }
    this.source = this.ctx.createMediaStreamSource(this.mic)
    this.capture = new AudioWorkletNode(this.ctx, 'capture')
    this.sink = this.ctx.createGain()
    this.sink.gain.value = 0
    this.source.connect(this.capture)
    this.capture.connect(this.sink)
    this.sink.connect(this.ctx.destination)
    this.capture.port.onmessage = (e: MessageEvent<Float32Array>) => this.onCapture(e.data)
    this.live = true
    this.send({ type: 'live', on: true })
    return true
  }

  public stopMic(notify = true): void {
    const wasLive = this.live
    this.live = false
    this.mic?.getTracks().forEach((t) => {
      t.onended = null
      t.stop()
    })
    try {
      this.source?.disconnect()
      this.capture?.disconnect()
      this.sink?.disconnect()
    } catch {
      // already disconnected
    }
    this.mic = null
    this.source = null
    this.capture = null
    this.sink = null
    this.pending = []
    this.pendingLength = 0
    if (wasLive && notify) this.interrupt()
  }

  public interrupt(): void {
    this.clearAudio()
    this.send({ type: 'interrupt' })
  }

  private async restartMic(): Promise<void> {
    this.stopMic(false)
    await this.startMic()
  }

  private onCapture(frame: Float32Array): void {
    this.pending.push(frame)
    this.pendingLength += frame.length
    if (this.pendingLength < 960) return // 40 ms
    const out = new Int16Array(this.pendingLength)
    let o = 0
    for (const chunk of this.pending) {
      for (let i = 0; i < chunk.length; i++) out[o++] = Math.max(-1, Math.min(1, chunk[i] ?? 0)) * 0x7fff
    }
    this.pending = []
    this.pendingLength = 0
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(out.buffer)
  }

  private ensureAudio(): Promise<void> {
    if (this.ready) {
      if (this.ctx?.state === 'suspended') void this.ctx.resume()
      return this.ready
    }
    const Ctx =
      window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctx) return Promise.resolve()
    const ctx = new Ctx({ sampleRate: 24000 })
    this.ctx = ctx
    const url = URL.createObjectURL(new Blob([WORKLET], { type: 'application/javascript' }))
    this.ready = ctx.audioWorklet.addModule(url).then(() => {
      this.player = new AudioWorkletNode(ctx, 'player', { numberOfInputs: 0, outputChannelCount: [1] })
      this.player.connect(ctx.destination)
      this.player.port.onmessage = (e: MessageEvent<{ playing: boolean }>) => {
        if (this.speaking !== e.data.playing) {
          this.speaking = e.data.playing
          this.listeners.forEach((l) => l({ type: this.speaking ? 'speaking' : 'quiet' }))
        }
      }
    })
    return this.ready
  }

  private async play(buffer: ArrayBuffer): Promise<void> {
    await this.ensureAudio()
    if (!this.player) return
    const pcm = new Int16Array(buffer)
    const f32 = new Float32Array(pcm.length)
    for (let i = 0; i < pcm.length; i++) f32[i] = (pcm[i] ?? 0) / 32768
    this.player.port.postMessage(f32, [f32.buffer])
  }

  private clearAudio(): void {
    this.player?.port.postMessage('clear')
  }
}
